import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { supabase } from "../lib/supabase";
import {
  normalizeAadhaar,
  maskAadhaar,
} from "../utils/kycUtils";
import { getPostLoginRoute } from "../utils/routeHelpers";

function VerifyOtp() {
  const navigate = useNavigate();
  const [otp, setOtp] = useState("");
  const [loading, setLoading] = useState(false);

  const createRoleRows = async (userId, payload) => {
    const {
      fullName,
      phone,
      email,
      role,
      city,
      businessName,
      aadhaarNumber,
      gstNumber,
      shopName,
      flatNo,
      streetArea,
      state,
      country,
      pincode,
      selectedSkills,
      displayUserId,
    } = payload;

    const { error: profileError } = await supabase.from("profiles").upsert([
      {
        id: userId,
        display_user_id: displayUserId,
        full_name: fullName,
        phone,
        email: email || null,
        role,
        city: city || null,
        is_phone_verified: true,
        is_email_verified: false,
      },
    ]);

    if (profileError) throw profileError;

    if (role === "customer") {
      const { error } = await supabase.from("customers").upsert([
        { profile_id: userId },
      ]);
      if (error) throw error;
    }

    if (role === "worker") {
      const normalizedAadhaar = normalizeAadhaar(aadhaarNumber);
      const skillsCsv = (selectedSkills || []).join(", ");

      const { error: kycError } = await supabase.from("worker_kyc").upsert({
        user_id: userId,
        full_name_as_per_aadhaar: fullName,
        aadhaar_masked: maskAadhaar(normalizedAadhaar),
        aadhaar_encrypted: null,
        aadhaar_last4: normalizedAadhaar.slice(-4),
        aadhaar_verified: false,
        kyc_status: "pending",
      });

      if (kycError) throw kycError;

      const { error } = await supabase.from("workers").upsert({
        profile_id: userId,
        full_name: fullName,
        phone,
        city,
        skill: skillsCsv,
        skills: skillsCsv,
        languages: "",
        nature_of_work: "",
        online_city: city || "",
        location: "",
        wallet_number: "",
        online_status: "offline",
        terms_accepted: false,
        wallet_balance: 0,
        is_kyc_complete: true,
        is_active: true,
        live_city_allowed: true,
      });

      if (error) throw error;
    }

    if (role === "shop_owner") {
      const normalizedAadhaar = normalizeAadhaar(aadhaarNumber);

      const { error: kycError } = await supabase.from("shop_owner_kyc").upsert({
        user_id: userId,
        full_name_as_per_aadhaar: fullName,
        aadhaar_masked: maskAadhaar(normalizedAadhaar),
        aadhaar_encrypted: null,
        aadhaar_last4: normalizedAadhaar.slice(-4),
        gst_number: gstNumber || null,
        gst_verified: false,
        aadhaar_verified: false,
        kyc_status: "pending",
      });

      if (kycError) throw kycError;

      const { data: ownerData, error: ownerError } = await supabase
        .from("shop_owners")
        .upsert({
          profile_id: userId,
          owner_name: fullName,
          phone,
          city,
          business_name: businessName || shopName || "",
          gst_number: gstNumber || null,
          flat_house_no: flatNo || null,
          street_area: streetArea || null,
          state: state || null,
          country: country || null,
          pincode: pincode || null,
          wallet_balance: 0,
          is_kyc_complete: true,
          is_active: true,
          live_city_allowed: true,
        })
        .select()
        .single();

      if (ownerError) throw ownerError;

      const { error: shopError } = await supabase.from("shops").upsert({
        shop_owner_id: ownerData.id,
        shop_name: shopName || businessName || "My Shop",
        gst_number: gstNumber || null,
        flat_house_no: flatNo || null,
        street_area: streetArea || null,
        city: city || null,
        state: state || null,
        country: country || null,
        pincode: pincode || null,
      });

      if (shopError) throw shopError;
    }
  };

  const handleVerify = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const pendingSignup = JSON.parse(
        sessionStorage.getItem("mistribazaarPendingSignup") ||
          localStorage.getItem("mistribazaarPendingSignup") ||
          "null"
      );

      if (!pendingSignup?.phone) {
        throw new Error("No pending signup found.");
      }

      const { data, error } = await supabase.auth.verifyOtp({
        phone: pendingSignup.phone,
        token: otp,
        type: "sms",
      });

      if (error) throw error;
      if (!data.user) throw new Error("Verification failed.");

      await createRoleRows(data.user.id, pendingSignup);

      sessionStorage.removeItem("mistribazaarPendingSignup");
      localStorage.removeItem("mistribazaarPendingSignup");

      navigate(getPostLoginRoute({ role: pendingSignup.role }));
    } catch (error) {
      console.error("Verify OTP error:", error);
      alert(error.message || "OTP verification failed.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={page}>
      <div style={card} className="otp-page__card">
        <h1>Verify Phone OTP</h1>
        <p style={{ color: "#d1d1d1" }}>
          Enter the OTP sent to your phone number.
        </p>

        <form onSubmit={handleVerify}>
          <input
            value={otp}
            onChange={(e) => setOtp(e.target.value)}
            placeholder="Enter OTP"
            required
            style={input}
          />

          <button type="submit" disabled={loading} style={button}>
            {loading ? "Verifying..." : "Verify OTP"}
          </button>
        </form>
      </div>
    </div>
  );
}

const page = {
  background: "radial-gradient(circle at top, #101010 0%, #000 55%)",
  minHeight: "100vh",
  padding: "40px 20px",
  color: "#fff",
  fontFamily: "Arial, sans-serif",
};

const card = {
  maxWidth: "520px",
  margin: "0 auto",
  background: "#111",
  padding: "24px",
  borderRadius: "18px",
  border: "1px solid #2a2a2a",
};

const input = {
  width: "100%",
  padding: "12px 14px",
  marginBottom: "14px",
  borderRadius: "10px",
  border: "1px solid #2a2a2a",
  background: "#0b0b0b",
  color: "#fff",
  boxSizing: "border-box",
};

const button = {
  width: "100%",
  padding: "12px 16px",
  borderRadius: "10px",
  border: "none",
  background: "#fff",
  color: "#000",
  fontWeight: "bold",
  cursor: "pointer",
};

export default VerifyOtp;
